import { useEffect, useId, useRef, useState } from 'react';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';

const sizes = { '6w': '6 wheeler', '8w': '8 wheeler', truck_trailer: 'Truck and trailer' };
export default function RegistrationInput({ value, onChange, onSelect, disabled }) {
  const listId = useId();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [status, setStatus] = useState('');
  const generation = useRef(0);
  useEffect(() => {
    const request = ++generation.current;
    setRows([]); setActive(-1);
    if (!value || disabled || !open) { setStatus(''); return; }
    setStatus('Searching registrations…');
    const timer = setTimeout(() => {
      api.get('/landfill/registrations', { params: { q: value }, timeout: 8000 })
        .then(({ data }) => {
          if (generation.current !== request) return;
          setRows(data); setStatus(data.length ? 'Choose the correct truck and carrier.' : 'No matching registration. Enter truck details below.');
        }).catch(() => { if (generation.current === request) setStatus('Lookup unavailable. You can enter details manually.'); });
    }, 150);
    return () => { ++generation.current; clearTimeout(timer); };
  }, [value, disabled, open]);
  const dismiss = () => { ++generation.current; setOpen(false); };
  const select = row => { dismiss(); onSelect(row); };
  return <div onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget)) dismiss(); }}
    onKeyDown={e => { if (e.key === 'Escape' && open) { e.preventDefault(); e.stopPropagation(); dismiss(); } }}>
    <Input id="lf-rego" role="combobox" aria-autocomplete="list" aria-expanded={open && rows.length > 0}
      aria-controls={listId} aria-activedescendant={open && active >= 0 ? `${listId}-${active}` : undefined}
      autoComplete="off" autoCapitalize="characters" maxLength={12} data-testid="landfill-rego-input"
      value={value} disabled={disabled} placeholder="ABC123" className="uppercase min-h-11"
      onFocus={() => setOpen(true)} onChange={e => { setOpen(true); onChange(e.target.value); }}
      onKeyDown={e => {
        if (['ArrowDown', 'ArrowUp'].includes(e.key)) {
          e.preventDefault(); setOpen(true);
          if (rows.length) setActive(i => e.key === 'ArrowDown' ? (i + 1) % rows.length : (i <= 0 ? rows.length - 1 : i - 1));
        }
        if (e.key === 'Enter' && open && active >= 0 && rows[active]) { e.preventDefault(); select(rows[active]); }
      }} />
    {open && <>
      <p role="status" className="mt-1 text-xs text-zinc-600">{status}</p>
      <ul id={listId} role="listbox" aria-label="Matching trucks and carriers" className="max-h-56 overflow-y-auto rounded border empty:border-0">
        {rows.map((row, i) => <li key={`${row.rego}-${row.client_id || row.company}-${i}`} role="presentation">
          <button type="button" role="option" id={`${listId}-${i}`} aria-selected={i === active}
            className={`w-full min-h-11 px-3 py-2 text-left text-sm focus-visible:ring-2 focus-visible:ring-blue-600 ${i === active ? 'bg-blue-100' : 'hover:bg-zinc-100'}`}
            onPointerDown={e => e.preventDefault()} onClick={() => select(row)}>
            <strong>{row.rego}</strong> · {row.company || 'Carrier unknown'}
            {row.truck_size && <span className="block text-xs">{sizes[row.truck_size] || row.truck_size}</span>}
          </button>
        </li>)}
      </ul>
    </>}
  </div>;
}
